<template>
  <div class="contents chat-page">
    <div class="title"><h2>채팅 관리</h2></div>
    <div class="box one chat-layout">
      <aside class="chat-list"><el-button v-if="hasNew" type="text" @click="refreshLatest">최근 채팅 보기</el-button><button v-for="chat in chats" :key="chat._id" :class="{ active: selected && selected._id === chat._id }" @click="selectChat(chat)"><strong>{{ customerLabel(chat) }}</strong><small>{{ chat.lastMessagePreview || "새 채팅" }}</small><el-badge v-if="chat.providerUnreadCount" :value="chat.providerUnreadCount" /></button><p v-if="!chats.length">채팅이 없습니다.</p><el-button v-if="hasMore" type="text" :loading="loadingMore" @click="loadMore">이전 채팅 더 보기</el-button></aside>
      <section class="chat-detail"><div v-if="selected" class="messages">
        <div ref="chatContent" class="chat-content" tabindex="0" aria-label="채팅 메시지" @wheel.passive="stopChatEntryScroll" @touchstart.passive="stopChatEntryScroll" @pointerdown="stopChatEntryScroll" @keydown="stopChatEntryScroll">
          <div v-for="message in messages" :key="message._id" :class="['message', { mine: message.senderRole === 'provider', 'message--image': message.attachment, 'message--estimate': isEstimateMessage(message) }]">
            <div v-if="isEstimateMessage(message)" class="estimate-context-card">
              <div class="estimate-context-card__heading">
                <span class="estimate-context-card__label">상담 상품</span>
                <span v-if="estimateContext(message).price" class="estimate-context-card__price">상담 당시 견적 {{ estimateContext(message).price }}</span>
              </div>
              <div v-if="estimateContext(message).products.length" class="estimate-context-card__products">
                <div v-for="(product, index) in estimateContext(message).products" :key="`${message._id}-product-${index}`" class="estimate-context-product">
                  <img v-if="product.imageUrl" :src="product.imageUrl" :alt="product.name || '상담 상품'" loading="lazy" @load="settleChatEntryScroll" @error="hideEstimateImage" />
                  <span>{{ product.name || '상품명 없음' }}</span>
                  <strong v-if="product.price">{{ product.price }}</strong>
                </div>
              </div>
              <span v-else class="estimate-context-card__empty">상품 정보 없음</span>
              <router-link v-if="estimateContextLink(message)" class="estimate-context-card__link" :to="estimateContextLink(message)">견적 상세 보기</router-link>
            </div>
            <img v-if="message.attachment" :src="message.attachment.url" alt="채팅 이미지" loading="lazy" referrerpolicy="no-referrer" @load="settleChatEntryScroll" @error="refreshImage(message)"/>
            <span v-if="showBody(message)">{{ message.body }}</span>
          </div>
        </div>
        <form @submit.prevent="send"><input ref="imageInput" class="chat-file-input" type="file" :accept="imageAccept" @change="selectImage"/><el-button v-if="imageEnabled" native-type="button" :disabled="sending" @click="openImagePicker">사진</el-button><div v-if="imagePreview" class="chat-image-preview"><img :src="imagePreview" alt="선택한 이미지 미리보기"/><button type="button" aria-label="선택한 이미지 제거" @click="clearImage">×</button></div><el-input v-model="draft" maxlength="1000" placeholder="답변을 입력해주세요."/><el-button type="primary" native-type="submit" :disabled="sending || (!draft.trim() && !selectedImage)">전송</el-button></form></div><p v-else class="empty">채팅할 고객을 선택해주세요.</p></section>
    </div>
  </div>
</template>
<script>
import { fetchProviderChats, fetchProviderChatMessages, markProviderChatRead, sendProviderChatImageMessage, sendProviderChatMessage, requestProviderChatTicket } from "@/api/providerDashboard";
import createManagementPageLifecycle from "@shared/createManagementPageLifecycle.cjs";
import { createChatRealtimeClient, chatResponse } from "@shared/chatRealtime.cjs";
import { createChatInbox, createChatRoom } from "@shared/chatState.cjs";
import { CHAT_IMAGE_ACCEPT, createChatMessagePayload, shouldShowChatBody, validateChatImage } from "@shared/chatImage.cjs";
export default {
  data: () => ({ chats: [], selected: null, messages: [], draft: "", sending: false, pendingMessage: null, selectedImage: null, imagePreview: "", imageAccept: CHAT_IMAGE_ACCEPT, imageEnabled: false, hasMore: false, hasNew: false, loadingMore: false }),
  ...createManagementPageLifecycle({ navbar: "9", navbarHook: "mounted", loadMethods: ["startChat"] }),
  beforeDestroy() { this._chatActive = false; this._chatTransport.stop(); window.removeEventListener("pageshow", this._onChatPageShow); this.clearImage(); },
  watch: { "$store.state.isLogin"(loggedIn) { if (!loggedIn && this._chatTransport) { this._chatActive = false; this._chatTransport.stop(); } } },
  methods: {
    showBody: shouldShowChatBody,
    isEstimateMessage(message) {
      const context = message && message.estimateContext;
      return message && message.messageType === "estimate" && context && context.version === 1;
    },
    estimateContext(message) {
      if (!this.isEstimateMessage(message)) return { products: [], price: "" };
      const context = message.estimateContext;
      return {
        products: Array.isArray(context.products) ? context.products.filter((product) => product && typeof product === "object") : [],
        price: typeof context.price === "string" ? context.price : "",
      };
    },
    hideEstimateImage(event) {
      if (event && event.target) event.target.hidden = true;
    },
    estimateContextLink(message) {
      if (!this.isEstimateMessage(message)) return "";
      const context = message.estimateContext;
      const id = context.estimateId || context.offerId;
      return id ? `/estimates/${encodeURIComponent(id)}` : "";
    },
    refreshImage(message) {
      this._imageRetryUrls = this._imageRetryUrls || new Set();
      const retryKey = message && message._id;
      if (!retryKey || this._imageRetryUrls.has(retryKey)) return;
      this._imageRetryUrls.add(retryKey);
      this._chatRoom.reset().catch((error) => this.chatError(error, false));
    },
    openImagePicker() { if (!this.sending) this.$refs.imageInput.click(); },
    selectImage(event) {
      const file = event.target.files && event.target.files[0];
      const error = validateChatImage(file);
      event.target.value = "";
      if (error) return this.$message.error(error);
      this.clearImage();
      this.selectedImage = file;
      this.imagePreview = URL.createObjectURL(file);
      this.pendingMessage = null;
    },
    clearImage() {
      if (this.imagePreview) URL.revokeObjectURL(this.imagePreview);
      this.selectedImage = null; this.imagePreview = ""; this.pendingMessage = null;
    },
    customerLabel(chat) { return (chat.customerId && (chat.customerId.username || chat.customerId.email)) || "고객"; },
    startChat() {
      this._chatActive = true;
      this._onChatPageShow = () => { this._chatEntryShown = false; this._chatEntrySettling = false; };
      window.addEventListener("pageshow", this._onChatPageShow);
      this._chatInbox = createChatInbox({ fetchPage: fetchProviderChats, active: () => this._chatActive,
        apply: ({ rows, hasMore, hasNew }) => {
          this.chats = rows; this.hasMore = hasMore; this.hasNew = hasNew;
          if (this.selected) this.selected = rows.find((row) => row._id === this.selected._id) || this.selected;
        },
      });
      this._chatTransport = createChatRealtimeClient({
        requestTicket: async () => chatResponse(await requestProviderChatTicket()).data,
        refresh: (changes) => this.loadChats(changes),
        onError: (error, auth) => this.chatError(error, auth),
      });
    },
    chatError(error, auth) {
      const payload = error.response && error.response.data;
      this.$message.error(payload && payload.message || error.message || "채팅 정보를 불러오지 못했습니다.");
      if (auth) this.$router.replace("/login");
    },
    async loadChats(changes) {
      await this._chatInbox.sync(changes);
      if (!this._chatActive) return;
      const restored = await this.restoreChatSelection();
      if (this._chatActive && this.selected && (restored || !changes || changes.some((change) => change.id === this.selected._id && change.kind === "message"))) await this._chatRoom.sync();
    },
    async restoreChatSelection() {
      const id = this.$store.state.selectedProviderChatId;
      if (!this._chatActive || this.selected || !id) return;
      // A previously selected room can be outside the first inbox page.
      const chat = this.chats.find((row) => row._id === id) || (chatResponse(await fetchProviderChats({ ids: id })).data || []).find((row) => row._id === id);
      if (!this._chatActive || this.selected || this.$store.state.selectedProviderChatId !== id) return;
      if (!chat) { this.$store.commit("SET_SELECTED_PROVIDER_CHAT", null); return; }
      this.selected = chat;
      this.prepareRoom(id);
      return true;
    },
    refreshLatest() { this._chatTransport.refresh(); },
    async loadMore() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      try { await this._chatInbox.more(); }
      catch (error) { this.chatError(error, [401, 403, 409].includes(Number(error.response && (error.response.data && error.response.data.status || error.response.status)))); }
      finally { this.loadingMore = false; }
    },
    selectChat(chat) {
      if (!this.selected || chat._id !== this.selected._id) {
        this.messages = [];
        this.draft = "";
        this.pendingMessage = null;
        this.clearImage();
      }
      this.selected = chat;
      this.$store.commit("SET_SELECTED_PROVIDER_CHAT", chat._id);
      this.prepareRoom(chat._id);
      this._chatTransport.refresh([{ id: chat._id, kind: "message" }]);
    },
    prepareRoom(id) {
      this._chatEntryShown = false;
      this._chatEntrySettling = false;
      const room = createChatRoom({
        fetchMessages: (params) => fetchProviderChatMessages(id, params), markRead: (snapshot) => markProviderChatRead(id, snapshot),
        active: () => this._chatRoom === room && this._chatActive && !document.hidden && this.selected && id === this.selected._id,
        unreadField: "providerUnreadCount",
        apply: (messages, conversation, payload) => {
          const last = messages[messages.length - 1], previous = this.messages[this.messages.length - 1];
          if (this._chatEntryShown && (last && last._id) !== (previous && previous._id)) this.stopChatEntryScroll();
          this.messages = messages;
          this.showLatestMessagesOnEntry();
          this.imageEnabled = !!(conversation && payload && payload.capabilities && payload.capabilities.chatImageUpload === 1);
        },
      });
      this._chatRoom = room;
    },
    showLatestMessagesOnEntry() {
      if (!this._chatActive || this._chatEntryShown || !this.messages.length) return;
      const room = this._chatRoom;
      this.$nextTick(() => {
        if (!this._chatActive || room !== this._chatRoom || this._chatEntryShown) return;
        const content = this.$refs.chatContent;
        if (!content) return;
        this._chatEntryShown = true;
        this._chatEntrySettling = true;
        content.scrollTop = content.scrollHeight;
      });
    },
    settleChatEntryScroll() {
      const content = this.$refs.chatContent;
      if (!this._chatActive || !this._chatEntrySettling || !content) return;
      content.scrollTop = content.scrollHeight;
    },
    stopChatEntryScroll() { this._chatEntrySettling = false; },
    scrollToBottom() {
      const content = this.$refs.chatContent;
      if (!this._chatActive || !content) return;
      this._chatEntrySettling = true;
      content.scrollTop = content.scrollHeight;
    },
    async send() {
      const body = this.draft.trim();
      const image = this.selectedImage;
      if ((!body && !image) || !this.selected || this.sending) return;
      const id = this.selected._id;
      if (!this.pendingMessage || this.pendingMessage.body !== body || this.pendingMessage.image !== image) this.pendingMessage = { body, image, clientMessageId: `${Date.now()}-${Math.random().toString(36).slice(2)}` };
      this.sending = true;
      try {
        const response = image ? await sendProviderChatImageMessage(id, createChatMessagePayload(this.pendingMessage)) : await sendProviderChatMessage(id, createChatMessagePayload(this.pendingMessage));
        chatResponse(response);
        if (!this._chatActive || !this.selected || id !== this.selected._id) return;
        if (this.draft.trim() === body) this.draft = "";
        if (this.selectedImage === image) this.clearImage(); else this.pendingMessage = null;
        await this._chatTransport.refresh([{ id, kind: "message" }]);
        if (!this._chatActive || !this.selected || id !== this.selected._id) return;
        this.$nextTick(() => this.scrollToBottom());
      } catch (error) { this.chatError(error, [401, 403].includes(Number(error.response && (error.response.data && error.response.data.status || error.response.status)))); }
      finally { this.sending = false; }
    },
  },
};
</script>
<style scoped>
.chat-page { align-content:flex-start; height:100vh; height:100dvh; min-height:0; overflow:hidden; }
.chat-layout { display:flex; padding:0; height:calc(100% - 120px);min-height:0; background:#fff; border:1px solid #ebe7f4; }.chat-list{overflow-y:auto;width:280px;flex:0 0 280px;min-width:0;border-right:1px solid #ebe7f4}.chat-list button{display:block;height:auto;min-height:72px;width:100%;padding:16px;border:0;border-bottom:1px solid #f1eff5;background:#fff;text-align:left}.chat-list button.active{background:#f6f2ff}.chat-list strong,.chat-list small{display:block}.chat-list small{margin-top:6px;color:#999;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.chat-detail{display:flex;flex-direction:column;flex:1;min-width:0;min-height:0;padding:20px;overflow:hidden}.messages{display:flex;min-height:0;flex:1;flex-direction:column;gap:10px}.message{display:flex;flex-shrink:0;align-self:flex-start;max-width:65%;flex-direction:column;gap:8px;padding:10px 14px;border-radius:14px;background:#f5f3f8;word-break:break-word;white-space:pre-wrap}.message.mine{align-self:flex-end;background:#8457d9;color:#fff}.message--image{padding:6px}.message>img{display:block;width:min(320px,100%);max-height:360px;border-radius:10px;object-fit:contain}.message>span{padding:4px 8px}.messages form{display:flex;flex-shrink:0;align-items:center;gap:8px;margin-top:auto}.messages .el-input{min-width:120px;flex:1}.chat-file-input{display:none}.chat-image-preview{position:relative;width:40px;height:40px;flex:0 0 40px}.chat-image-preview img{width:100%;height:100%;border-radius:6px;object-fit:cover}.chat-image-preview button{display:flex;position:absolute;top:-7px;right:-7px;width:20px;height:20px;align-items:center;justify-content:center;padding:0;border:1px solid #ddd;border-radius:50%;background:#fff;color:#333;font-size:16px;line-height:1}.empty,.chat-list p{padding:40px;text-align:center;color:#999}
.message--estimate{box-sizing:border-box;min-width:0;padding:10px;background:var(--color-white,#fff);border:1px solid var(--color-border,#e5def2);white-space:normal}.estimate-context-card{width:min(100%,420px);min-width:0}.estimate-context-card__heading{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px;color:var(--color-text-strong,#4b386d)}.estimate-context-card__label{font-size:13px;font-weight:700}.estimate-context-card__price{min-width:0;font-size:13px;font-weight:700;color:var(--color-primary,#8457d9);text-align:right;overflow-wrap:anywhere}.estimate-context-card__products{display:flex;flex-direction:column;gap:8px}.estimate-context-product{display:flex;align-items:center;gap:8px;min-width:0;font-size:13px;color:var(--color-text-strong,#333)}.estimate-context-product img{width:44px;height:44px;flex:0 0 44px;border-radius:6px;object-fit:cover;background:var(--color-surface-muted,#f5f3f8)}.estimate-context-product span{min-width:0;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.estimate-context-product strong{flex:0 1 40%;max-width:40%;margin-left:auto;white-space:normal;overflow-wrap:anywhere;text-align:right;font-size:12px;color:var(--color-text-secondary,#666)}.estimate-context-card__empty{display:block;padding:8px 0;color:var(--color-text-secondary,#999);font-size:12px}.estimate-context-card__link{display:inline-block;margin-top:10px;color:var(--color-primary-strong,#6b43b4);font-size:12px;font-weight:700;text-decoration:underline}
.chat-content { display:flex; flex-direction:column; flex:1; min-height:0; gap:10px; overflow-y:auto; overscroll-behavior:contain; }
@media (max-width:720px){.chat-layout{height:calc(100% - 90px);flex-direction:column}.chat-list{width:100%;flex:0 0 136px;border-right:0;border-bottom:1px solid #ebe7f4}.chat-detail{min-height:0;padding:12px}.message{max-width:100%}.estimate-context-card{width:100%}.estimate-context-card__heading{flex-wrap:wrap}}
</style>
